import classNames from 'classnames/bind';
import React, { useCallback, useEffect, useState } from 'react';
import Modal from '../Modal';
import useDidMount from '../../hooks/useDidMount';
import Wheel from './Wheel';
import styles from './PickerView.module.scss';

const cn = classNames.bind(styles);

export interface DataSource {
  value: string | number;
  label: string | number;
  children?: DataSource[];
}

interface Props {
  data: DataSource[] | Array<DataSource[]>; // 支持多列或单列及内联
  value: Array<string | number | undefined>;
  onChange: (value: Array<string | number>) => void;
  multiple?: boolean; // 是否有多列选择
  cascade?: boolean; // 是否级联
  cols?: number; // 在级联情况下传递
  children: React.ReactElement;
  title?: string;
  onScroll?: (valueList: Array<string | number | undefined>) => void; // 列表滚动
  wrapperClassName?: string;
}

export default function PickerView(props: Props) {
  const {
    multiple = false,
    cascade = false,
    onChange,
    children,
    value,
    data,
    cols,
    title,
  } = props;
  const [open, setOpen] = useState<boolean>(false);
  const [valueList, setValueList] =
    useState<Array<string | number | undefined>>(value);
  const handleOpen = useCallback(function () {
    setOpen(true);
  }, []);
  const child = React.cloneElement(children, {
    onClick: handleOpen,
  });

  useEffect(
    function () {
      if (!open) {
        document.documentElement.classList.add(styles.wheel_overflowHidden);
      } else {
        setValueList(value);
        document.documentElement.classList.remove(styles.wheel_overflowHidden);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open]
  );

  useDidMount(function () {
    return function () {
      if (
        document.documentElement.className.includes(styles.wheel_overflowHidden)
      ) {
        document.documentElement.classList.remove(styles.wheel_overflowHidden);
      }
    };
  });

  const handleScroll = useCallback(
    function (value: string | number, index: number) {
      const list: Array<string | number | undefined> = valueList.slice();

      list[index] = value;
      setValueList(list);
      if (props.onScroll) {
        props.onScroll(list);
      }
    },
    [props, valueList]
  );
  // 级联滚动时将后续value置为undefined
  const handleCascadeScroll = useCallback(
    function (value: string | number, index: number) {
      const list: Array<string | number | undefined> = valueList.map(
        (item, i) => {
          if (index === i) {
            return value;
          } else if (i > index) {
            return undefined;
          }

          return item;
        }
      );

      setValueList(list);
      if (props.onScroll) {
        props.onScroll(list);
      }
    },
    [props, valueList]
  );

  const getDataFromCascade = useCallback(
    function (index: number) {
      let source: DataSource[] = data as DataSource[];

      for (let i = 0; i < index; i++) {
        const currentIndex = Math.max(
          source.findIndex(item => item.value === valueList[i]),
          0
        );

        source = source[currentIndex]
          ? (source[currentIndex].children as DataSource[])
          : [];
      }

      return source;
    },
    [data, valueList]
  );

  const renderWheels = useCallback(
    function () {
      if (cascade) {
        if (cols === undefined) {
          console.error('缺失参数cols,cascade需搭配cols实现');

          return null;
        }
        let result = [];

        for (let i = 0; i < cols!; i++) {
          const currentData = getDataFromCascade(i);

          result.push(
            <Wheel
              key={i}
              data={currentData}
              value={valueList[i]}
              onScroll={value => handleCascadeScroll(value, i)}
            />
          );
        }

        return <div className={styles.picker_multiple_wrapper}>{result}</div>;
      } else if (multiple) {
        return (
          <div className={styles.picker_multiple_wrapper}>
            {(data as Array<DataSource[]>).map((item, index) => {
              const value = valueList[index];

              return (
                <Wheel
                  key={index}
                  data={item}
                  value={value}
                  onScroll={value => handleScroll(value, index)}
                />
              );
            })}
          </div>
        );
      } else {
        return (
          <Wheel
            data={data as DataSource[]}
            value={valueList[0]}
            onScroll={value => handleScroll(value, 0)}
          />
        );
      }
    },
    [
      cascade,
      cols,
      data,
      getDataFromCascade,
      handleCascadeScroll,
      handleScroll,
      multiple,
      valueList,
    ]
  );
  const handleClose = useCallback(function () {
    setOpen(false);
  }, []);

  const handleOk = useCallback(
    function () {
      const result = valueList.map((value, index) => {
        if (value === undefined) {
          if (cascade) {
            return getDataFromCascade(index)[0].value;
          } else if (multiple) {
            return (data as DataSource[][])[index][0].value;
          } else {
            return (data as DataSource[])[index].value;
          }
        }

        return value;
      });

      onChange(result);
      setOpen(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [valueList]
  );

  return (
    <>
      {child}
      <Modal open={open} archor="bottom" onClose={handleClose}>
        <div className={cn(styles.picker_wrapper, props.wrapperClassName)}>
          <div className={styles.picker_header}>
            <span onClick={handleClose}>取消</span>
            {title ? (
              <span className={styles.picker_header_title}>{title}</span>
            ) : null}
            <span onClick={handleOk}>确定</span>
          </div>
          {renderWheels()}
        </div>
      </Modal>
    </>
  );
}
